import {selectedLocationFields,municipalityFields} from '../../../../geo/locationFields';
import { Select as CasoSelect } from 'antd';
// src/components/Clients/Form/ClientForm.js

import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Form, Row, Col, message, Spin } from "antd";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import api from "../../../../axiosConfig";
import GeneralInfo from "./GeneralInfo";
import AddressInfo from "./AddressInfo";
import AdditionalInfo from "./AdditionalInfo";
import ImportantDates from "./ImportantDates";
import FuecInfo from "./FuecInfo";
import MapDialog from "../../../../Resources/GoogleApi/MapDialog";
import useFetchDepartamentosYZonas from "../../../../hooks/useFetchDepartamentosYZonas";
import useHandleDepartamentoChange from "../../../../hooks/useHandleDepartamentoChange";
import { formatClientDates } from "./utils/formatDates";
import { ValidationError, GeneralError } from "./utils/apiClient";
import {
  UserAddOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";

const ClientForm = ({
  open,
  handleClose,
  fetchClients,
  initialData = null,
  isEdit = false,
}) => {
  const [form] = Form.useForm();
  const { departamentos, zonas, loadingDepartamentos, loadingZonas } =
    useFetchDepartamentosYZonas();
  const { ciudades, loadingCiudades, handleDepartamentoChange } =
    useHandleDepartamentoChange(form);

  const [isMapOpen, setIsMapOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para manejo de cliente TAI
  const [esTai, setEsTai] = useState(false);
  const [clientesEps, setClientesEps] = useState([]);
  const [loadingClientesEps, setLoadingClientesEps] = useState(false);
  const [clientesEpsSeleccionados, setClientesEpsSeleccionados] = useState([]);
  const [clientesEpsOriginales, setClientesEpsOriginales] = useState([]); // Para edición

  // Cargar clientes EPS (no TAI) para relacionar
  const fetchClientesEps = useCallback(async () => {
    try {
      setLoadingClientesEps(true);
      const response = await api.get("/clientes/list");
      // Filtrar solo clientes que NO son TAI
      const clientesNoTai = response.data.filter(c => !c.es_cliente_tai);
      setClientesEps(clientesNoTai);
    } catch (error) {
      console.error("Error al cargar clientes EPS:", error);
    } finally {
      setLoadingClientesEps(false);
    }
  }, []);

  // Handler para cambio de switch TAI
  const handleTaiChange = (checked) => {
    setEsTai(checked);
    if (checked) {
      // Cargar clientes EPS cuando se activa TAI
      fetchClientesEps();
    } else {
      // Limpiar selección cuando se desactiva
      setClientesEpsSeleccionados([]);
      form.setFieldsValue({ clientes_eps_ids: [] });
    }
  };

  // Handler para cambio de clientes EPS seleccionados
  const handleClientesEpsChange = (values) => {
    setClientesEpsSeleccionados(values);
  };

  // Cargar relaciones EPS existentes para un cliente TAI (para edición)
  const fetchRelacionesEpsExistentes = useCallback(async (taiId) => {
    try {
      const response = await api.get(`/clientes/eps/by-tai/${taiId}`);
      const epsIds = response.data.map(eps => eps.id);
      setClientesEpsSeleccionados(epsIds);
      setClientesEpsOriginales(epsIds); // Guardar originales para comparar al guardar
      form.setFieldsValue({ clientes_eps_ids: epsIds });
    } catch (error) {
      console.error("Error al cargar relaciones EPS existentes:", error);
    }
  }, [form]);

  // Función para formatear datos iniciales
  const formatInitialData = (data) => ({
    ...data,
    fecha_ultima_revision: data.fecha_ultima_revision
      ? dayjs(data.fecha_ultima_revision)
      : null,
    fecha_proxima_revision: data.fecha_proxima_revision
      ? dayjs(data.fecha_proxima_revision)
      : null,
    fecha_contrato_firmado: data.fecha_contrato_firmado
      ? dayjs(data.fecha_contrato_firmado)
      : null,
    fecha_vencimiento_contrato: data.fecha_vencimiento_contrato
      ? dayjs(data.fecha_vencimiento_contrato)
      : null,
    fecha_creacion: data.fecha_creacion ? dayjs(data.fecha_creacion) : null,
    fecha_revision_contrato: data.fecha_revision_contrato
      ? dayjs(data.fecha_revision_contrato)
      : null,
    fecha_aprobacion: data.fecha_aprobacion
      ? dayjs(data.fecha_aprobacion)
      : null,
  });

  useEffect(() => {
    const initializeForm = async () => {
      if (initialData) {
        try {
          setFormLoading(true);
          await handleDepartamentoChange(initialData.departamento_id);
          const formattedData = formatInitialData(initialData);
          form.setFieldsValue(formattedData);
          // Inicializar estado TAI si el cliente es TAI
          if (initialData.es_cliente_tai) {
            setEsTai(true);
            await fetchClientesEps();
            // Cargar relaciones EPS existentes para este cliente TAI
            await fetchRelacionesEpsExistentes(initialData.id);
          }
        } catch (error) {
          console.error("Error al inicializar el formulario:", error);
          message.error("Error al inicializar el formulario");
        } finally {
          setFormLoading(false);
        }
      } else {
        form.resetFields();
        form.setFieldsValue({ pais: "Colombia" });
        // Resetear estado TAI
        setEsTai(false);
        setClientesEpsSeleccionados([]);
        setClientesEpsOriginales([]);
      }
    };

    initializeForm();
  }, [initialData, form, handleDepartamentoChange, fetchClientesEps, fetchRelacionesEpsExistentes]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const formattedValues = formatClientDates(values);
      formattedValues.caso_geografico = values.caso_geografico ?? null;

      // Crear o actualizar el cliente
      let clienteId = initialData?.id;

      if (isEdit && initialData) {
        await api.put(`/clientes/${initialData.id}`, formattedValues);
        message.success("Cliente actualizado exitosamente");
      } else {
        // Si es nuevo cliente, obtener el ID del cliente creado
        const response = await api.post("/clientes", formattedValues);
        clienteId = response.data.cliente?.id;
        message.success("Cliente agregado exitosamente");
      }

      // Si es cliente TAI, sincronizar relaciones con clientes EPS
      if (esTai && clienteId) {
        try {
          // 1. Eliminar relaciones de EPS que ya no están seleccionados (solo en edición)
          if (isEdit) {
            const epsAEliminar = clientesEpsOriginales.filter(
              epsId => !clientesEpsSeleccionados.includes(epsId)
            );
            for (const epsId of epsAEliminar) {
              const relacionesResp = await api.get(`/clientes/${epsId}/relaciones-tai`);
              const relacionesActuales = relacionesResp.data.clientes_tai_relacionados || [];
              // Quitar este TAI de las relaciones del EPS
              const nuevosIds = relacionesActuales
                .map(r => r.id)
                .filter(id => id !== clienteId);
              await api.post(`/clientes/${epsId}/relaciones-tai`, {
                clientes_tai_ids: nuevosIds
              });
            }
          }

          // 2. Agregar relaciones a los EPS seleccionados
          for (const epsId of clientesEpsSeleccionados) {
            const relacionesResp = await api.get(`/clientes/${epsId}/relaciones-tai`);
            const relacionesActuales = relacionesResp.data.clientes_tai_relacionados || [];
            const idsActuales = relacionesActuales.map(r => r.id);

            // Agregar este TAI si no existe ya
            if (!idsActuales.includes(clienteId)) {
              const nuevosIds = [...idsActuales, clienteId];
              await api.post(`/clientes/${epsId}/relaciones-tai`, {
                clientes_tai_ids: nuevosIds
              });
            }
          }
        } catch (relError) {
          console.error("Error al sincronizar relaciones TAI:", relError);
          message.warning("Cliente guardado, pero hubo un error al guardar algunas relaciones TAI");
        }
      }

      fetchClients();
      handleClose();
    } catch (error) {
      console.log("Error capturado:", error);
      if (error instanceof ValidationError) {
        form.setFields(
          Object.keys(error.fieldErrors).map((field) => ({
            name: field,
            errors: [error.fieldErrors[field]],
          }))
        );

        Object.values(error.fieldErrors).forEach((errorMsg) => {
          message.error(errorMsg);
        });
      } else if (error instanceof GeneralError) {
        message.error(error.message);
      } else if (error.response?.data?.errors) {
        // Manejar errores de validación del backend
        const errors = error.response.data.errors;
        Object.values(errors).forEach((errorMsg) => {
          message.error(Array.isArray(errorMsg) ? errorMsg[0] : errorMsg);
        });
      } else {
        message.error("Error al guardar el cliente");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMapDialogOpen = () => {
    setIsMapOpen(true);
  };

  const handleMapDialogClose = () => {
    setIsMapOpen(false);
  };

  const handleSelect = async (direccion, localidad, coordenadas, details) => {
    const municipality = municipalityFields(details);
    if (municipality.departamento_id) await handleDepartamentoChange(municipality.departamento_id);
    form.setFieldsValue({...selectedLocationFields(direccion, localidad, coordenadas, details), ...municipality});
    setIsMapOpen(false);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div
          style={{
            background: "linear-gradient(90deg, #0a2e5c, #004d99)",
            padding: "12px",
            borderRadius: "8px 8px 0 0",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            color: "white",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {isEdit ? (
            <>
              <EditOutlined style={{ marginRight: "8px" }} />
              Editar Cliente
            </>
          ) : (
            <>
              <UserAddOutlined style={{ marginRight: "8px" }} />
              Agregar Cliente
            </>
          )}
        </div>
      }
      footer={null}
      width={1000}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Spin spinning={formLoading} tip="Cargando formulario...">
          <Row gutter={24}>
            {/* Columna Izquierda */}
            <Col xs={24} sm={24} md={12}>
              <GeneralInfo
                esTai={esTai}
                onTaiChange={handleTaiChange}
                clientesEps={clientesEps}
                loadingClientesEps={loadingClientesEps}
                clientesEpsSeleccionados={clientesEpsSeleccionados}
                onClientesEpsChange={handleClientesEpsChange}
              />
              <FuecInfo />
              <AdditionalInfo />
            </Col>

            {/* Columna Derecha */}
            <Col xs={24} sm={24} md={12}>
              <AddressInfo
                zonas={zonas}
                departamentos={departamentos}
                ciudades={ciudades}
                loadingZonas={loadingZonas}
                loadingDepartamentos={loadingDepartamentos}
                loadingCiudades={loadingCiudades}
                handleMapDialogOpen={handleMapDialogOpen}
                onDepartamentoChange={handleDepartamentoChange}
                onZonaChange={(value) => {
                  console.log("Zona seleccionada:", value);
                }}
              />
              <ImportantDates />
            </Col>
          </Row>

          {/* Botones de Acción */}
          <Form.Item name="caso_geografico" label="Caso geográfico de autorizaciones" help="Origen y destino se configuran de forma independiente en cada autorización.">
          <CasoSelect allowClear placeholder="Pendiente de configuración" options={[
            {value: 1, label: 'Caso 1: un departamento y una ciudad'},
            {value: 2, label: 'Caso 2: varios departamentos y ciudades'},
            {value: 3, label: 'Caso 3: un departamento y varias ciudades'},
          ]} />
        </Form.Item>
        <Form.Item>
            <Row justify="end">
              <Col>
                <Button
                  onClick={() => {
                    handleClose();
                    form.resetFields();
                  }}
                  style={{
                    backgroundColor: "#d32f2f",
                    color: "white",
                    borderColor: "#d32f2f",
                    borderRadius: "4px",
                    transition: "background-color 0.3s ease",
                    marginRight: "8px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#b71c1c")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#d32f2f")
                  }
                  icon={<CloseOutlined />}
                >
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  style={{
                    backgroundColor: "#0a2e5c",
                    color: "white",
                    borderColor: "#0a2e5c",
                    borderRadius: "4px",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#004080")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#0a2e5c")
                  }
                  icon={<SaveOutlined />}
                >
                  {isEdit ? "Guardar Cambios" : "Guardar"}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Spin>
      </Form>

      {/* Integrar el MapDialog */}
      <MapDialog
        open={isMapOpen}
        onClose={handleMapDialogClose}
        onSelect={handleSelect}
        initialCoords={form.getFieldValue("coordenadas")}
      />
    </Modal>
  );
};

ClientForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  fetchClients: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default ClientForm;
